import { supabase } from "../supabase/supabase";
import {
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  apiDelete,
  makeAuthenticatedRequest,
} from "../services/restApiService";
import {
  OrganisationUsersData,
  OrganizationData,
  InviteUserRequest,
  StaffListFilters,
} from "../types/types";

// Bulk staging is deliberately serial so licence allocation and its per-row
// result remain deterministic. It therefore needs a longer limit than the
// 10-second default used for ordinary requests.
const BULK_INVITE_REQUEST_TIMEOUT_MS = 120_000;

// The bulk endpoint activates each selected staff member serially. A client
// timeout leaves completion ambiguous, so both activation endpoints use the
// same bounded 120-second window as staged CSV upload before the UI asks the
// administrator to refresh.
const STAFF_ACTIVATION_REQUEST_TIMEOUT_MS = 120_000;
const STAFF_BULK_BATCH_SIZE = 25;

type BulkInviteResponse = {
  total: number;
  success: number;
  failed: number;
  results: Array<{ email: string; success: boolean; message?: string }>;
};

export type PartialBatchError<T> = Error & { partialResult: T };

function withPartialResult<T>(
  message: string,
  partialResult: T,
  cause: unknown,
): PartialBatchError<T> {
  const causeMessage = cause instanceof Error ? cause.message : String(cause);
  return Object.assign(new Error(`${message}: ${causeMessage}`), {
    partialResult,
  });
}

function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error ?? new Error("CSV read failed"));
    reader.readAsText(file);
  });
}

function splitCsvRecords(csv: string): string[] {
  const records: string[] = [];
  let record = "";
  let quoted = false;

  for (let index = 0; index < csv.length; index += 1) {
    const character = csv[index];
    if (character === '"') {
      record += character;
      if (quoted && csv[index + 1] === '"') {
        record += csv[index + 1];
        index += 1;
      } else {
        quoted = !quoted;
      }
      continue;
    }
    if (!quoted && (character === "\n" || character === "\r")) {
      if (character === "\r" && csv[index + 1] === "\n") index += 1;
      records.push(record);
      record = "";
      continue;
    }
    record += character;
  }
  records.push(record);
  return records;
}

/**
 * List organisations accessible to the current user.
 * Super Admins see all active organisations; regular admins see only their own.
 */
export async function listAccessibleOrganisations(): Promise<
  { id: string; name: string }[]
> {
  return apiGet<{ id: string; name: string }[]>("/admin/organisations");
}

/**
 * Get organization details by ID
 * @param organisationId - Organization ID
 * @returns Promise<OrganizationData>
 */
export async function getOrganisationDetails(
  organisationId: string,
): Promise<OrganizationData> {
  return apiGet<OrganizationData>(
    `/admin/organisations/${organisationId}/details`,
  );
}

/**
 * Get users for a specific organization
 * @param organisationId - Organization ID
 * @param page - Page number
 * @param pageSize - Number of items per page
 * @returns Promise<OrganizationUsersApiResponse>
 */
export async function getOrganisationUsers(
  organisationId: string,
  page: number = 1,
  pageSize: number = 10,
  filters: StaffListFilters = {},
): Promise<OrganisationUsersData> {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.status?.length) params.set("status", filters.status.join(","));

  return apiGet<OrganisationUsersData>(
    `/admin/organisations/${organisationId}/users?${params}`,
  );
}

/**
 * Deactivate a user from an organization
 * @param organisationId - Organization ID
 * @param userId - User ID to deactivate
 * @returns Promise<void>
 */
export async function deactivateUser(
  organisationId: string,
  userId: string,
): Promise<void> {
  return apiDelete(`/admin/organisations/${organisationId}/users/${userId}`);
}

/**
 * Invite a new user to an organization
 * @param organisationId - Organization ID
 * @param userData - User data for invitation
 * @returns Promise<void>
 */
export async function inviteUser(
  organisationId: string,
  userData: InviteUserRequest,
): Promise<void> {
  return apiPost(
    `/admin/organisations/${organisationId}/users/invite`,
    userData,
  );
}

/**
 * Upload organization branding logo
 * @param organisationId - Organization ID
 * @param logoFile - Logo file to upload
 * @returns Promise<{ logoUrl: string }>
 */
export async function uploadOrganisationBranding(
  organisationId: string,
  logoFile: File,
): Promise<{ logoUrl: string }> {
  const formData = new FormData();
  formData.append("file", logoFile);

  return makeAuthenticatedRequest<{ logoUrl: string }>(
    `/admin/organisations/${organisationId}/branding`,
    {
      method: "POST",
      data: formData,
    },
  );
}

/**
 * Get all unique tags used in an organization
 * @param organisationId - Organization ID
 * @returns Promise<{ tags: string[] }>
 */
export async function getOrganisationTags(
  organisationId: string,
): Promise<{ tags: string[] }> {
  return apiGet<{ tags: string[] }>(
    `/super-admin/organisations/${organisationId}/users/tags`,
  );
}

/**
 * Update a user's tags
 * @param organisationId - Organization ID
 * @param userId - User ID
 * @param tags - Array of tag strings
 * @returns Promise with updated tags
 */
export async function updateUserTags(
  organisationId: string,
  userId: string,
  tags: string[],
): Promise<{
  success: boolean;
  userId: string;
  email: string;
  tags: string[];
  normalized_tags: string[];
}> {
  return apiPut(
    `/super-admin/organisations/${organisationId}/users/${userId}/tags`,
    { tags },
  );
}

/**
 * Update a user's role within an organization
 * @param organisationId - Organization ID
 * @param userId - User ID
 * @param role - New role ('learner' | 'admin')
 * @returns Promise with success status and message
 */
export async function updateUserRole(
  organisationId: string,
  userId: string,
  role: "learner" | "admin",
): Promise<{ success: boolean; message: string }> {
  return apiPatch(`/admin/organisations/${organisationId}/users/${userId}`, {
    role,
  });
}

/**
 * Bulk invite users via CSV upload
 * @param organisationId - Organization ID
 * @param file - CSV file containing user data
 * @returns Promise with upload results
 */
export async function bulkInviteUsers(
  organisationId: string,
  file: File,
): Promise<BulkInviteResponse> {
  const records = splitCsvRecords(await readFileText(file)).filter(
    (record, index) => index === 0 || record.length > 0,
  );
  const header = records[0] ?? "";
  const rows = records.slice(1);
  const aggregate: BulkInviteResponse = {
    total: 0,
    success: 0,
    failed: 0,
    results: [],
  };

  // Preserve the server's canonical empty/header-only CSV validation instead
  // of turning it into a client-side zero-row success.
  if (rows.length === 0) {
    const formData = new FormData();
    formData.append("file", file);
    return makeAuthenticatedRequest<BulkInviteResponse>(
      `/admin/organisations/${organisationId}/users/invite/bulk`,
      {
        method: "POST",
        data: formData,
        timeout: BULK_INVITE_REQUEST_TIMEOUT_MS,
      },
    );
  }

  for (let offset = 0; offset < rows.length; offset += STAFF_BULK_BATCH_SIZE) {
    const batchRows = rows.slice(offset, offset + STAFF_BULK_BATCH_SIZE);
    const batchFile = new File(
      [`${header}\n${batchRows.join("\n")}`],
      file.name,
      { type: file.type || "text/csv" },
    );
    const formData = new FormData();
    formData.append("file", batchFile);
    let result: BulkInviteResponse;
    try {
      result = await makeAuthenticatedRequest<BulkInviteResponse>(
        `/admin/organisations/${organisationId}/users/invite/bulk`,
        {
          method: "POST",
          data: formData,
          timeout: BULK_INVITE_REQUEST_TIMEOUT_MS,
        },
      );
    } catch (error) {
      if (aggregate.total === 0) throw error;
      throw withPartialResult(
        "A later upload batch failed after earlier staff were staged",
        aggregate,
        error,
      );
    }
    aggregate.total += result.total;
    aggregate.success += result.success;
    aggregate.failed += result.failed;
    aggregate.results.push(...result.results);
  }

  return aggregate;
}

export type WelcomeEmailOutcome =
  | "sent"
  | "skipped_allowlist"
  | "blocked_activation_recovery"
  | "failed";

export interface ActivationResult {
  success: boolean;
  userId: string;
  email: string;
  status: "invited";
  emailOutcome: WelcomeEmailOutcome;
}

export interface BulkActivationProgress {
  batchIndex: number;
  batchSize: number;
  activatedSoFar: number;
  remaining?: number;
}

export interface BulkActivationOptions {
  onBatchProgress?: (progress: BulkActivationProgress) => void;
  shouldStop?: () => boolean;
}

export interface BulkActivationResponse {
  stoppedByOperator?: boolean;
  total: number;
  success: number;
  failed: number;
  results: Array<{
    userId: string;
    email?: string;
    success: boolean;
    emailOutcome?: WelcomeEmailOutcome;
    message?: string;
  }>;
  remainingNotActivated?: number;
  deliveryEvidenceUnconfirmed?: boolean;
}

export type BulkActivationInput =
  | { userIds: string[]; allNotActivated?: never }
  | { userIds?: never; allNotActivated: true };

function hasInvalidActivationEvidence(
  result: BulkActivationResponse,
  expectedUserIds?: string[],
): boolean {
  const successfulResults = result.results.filter((item) => item.success);
  const resultUserIds = result.results.map((item) => item.userId);
  const uniqueResultUserIds = new Set(
    resultUserIds.filter(
      (userId) => typeof userId === "string" && userId.trim().length > 0,
    ),
  );
  const expectedUserIdSet = expectedUserIds
    ? new Set(expectedUserIds)
    : undefined;
  return (
    result.results.length !== result.total ||
    successfulResults.length !== result.success ||
    result.success + result.failed !== result.total ||
    uniqueResultUserIds.size !== result.total ||
    (expectedUserIdSet !== undefined &&
      (expectedUserIdSet.size !== result.total ||
        resultUserIds.some((userId) => !expectedUserIdSet.has(userId))))
  );
}

function hasUnsentActivationEmail(result: BulkActivationResponse): boolean {
  return result.results.some(
    (item) => item.success && item.emailOutcome !== "sent",
  );
}

function isActivationResultRow(
  value: unknown,
): value is BulkActivationResponse["results"][number] {
  if (!value || typeof value !== "object") return false;
  const row = value as { userId?: unknown; success?: unknown };
  return typeof row.userId === "string" && typeof row.success === "boolean";
}

function normalizeActivationEvidence(
  response: BulkActivationResponse,
  expectedUserIds?: string[],
  requireNonEmpty = false,
): BulkActivationResponse {
  const candidate =
    response as unknown as Partial<BulkActivationResponse> | null;
  const hasValidCount = (value: unknown): value is number =>
    typeof value === "number" && Number.isInteger(value) && value >= 0;
  const candidateTotal = candidate?.total;
  const candidateSuccess = candidate?.success;
  const candidateFailed = candidate?.failed;
  const candidateRemaining = candidate?.remainingNotActivated;
  const candidateResults = candidate?.results as unknown;
  const hasResultArray = Array.isArray(candidateResults);
  const normalizedResults = hasResultArray
    ? candidateResults.filter(isActivationResultRow)
    : [];
  const hasValidResultRows =
    hasResultArray && normalizedResults.length === candidateResults.length;
  const normalized: BulkActivationResponse = {
    total: hasValidCount(candidateTotal) ? candidateTotal : 0,
    success: hasValidCount(candidateSuccess) ? candidateSuccess : 0,
    failed: hasValidCount(candidateFailed) ? candidateFailed : 0,
    results: normalizedResults,
    ...(hasValidCount(candidateRemaining)
      ? { remainingNotActivated: candidateRemaining }
      : {}),
  };
  if (
    !candidate ||
    !hasValidCount(candidateTotal) ||
    !hasValidCount(candidateSuccess) ||
    !hasValidCount(candidateFailed) ||
    !hasResultArray ||
    !hasValidResultRows ||
    (requireNonEmpty &&
      (normalized.total === 0 || !hasValidCount(candidateRemaining))) ||
    hasInvalidActivationEvidence(normalized, expectedUserIds)
  ) {
    normalized.deliveryEvidenceUnconfirmed = true;
  }
  return normalized;
}

export async function activateUser(
  organisationId: string,
  userId: string,
): Promise<ActivationResult> {
  return makeAuthenticatedRequest<ActivationResult>(
    `/admin/organisations/${organisationId}/users/${userId}/activate`,
    {
      method: "POST",
      data: {},
      timeout: STAFF_ACTIVATION_REQUEST_TIMEOUT_MS,
    },
  );
}

export async function bulkActivateUsers(
  organisationId: string,
  input: BulkActivationInput,
  options?: BulkActivationOptions,
): Promise<BulkActivationResponse> {
  const hasUserIdsProperty = Object.prototype.hasOwnProperty.call(
    input,
    "userIds",
  );
  const hasAllNotActivatedProperty = Object.prototype.hasOwnProperty.call(
    input,
    "allNotActivated",
  );
  if (hasUserIdsProperty === hasAllNotActivatedProperty) {
    throw new Error("Bulk activation requires exactly one selection mode");
  }
  if (hasUserIdsProperty) {
    const selectedUserIds = input.userIds;
    if (
      selectedUserIds &&
      new Set(selectedUserIds).size !== selectedUserIds.length
    ) {
      throw new Error("Bulk activation selected user IDs must be distinct");
    }
    // Preserve server-side validation for missing/empty selections and avoid
    // changing the ordinary one-request path for selections within the cap.
    if (!selectedUserIds || selectedUserIds.length <= STAFF_BULK_BATCH_SIZE) {
      const normalized = normalizeActivationEvidence(
        await makeAuthenticatedRequest<BulkActivationResponse>(
          `/admin/organisations/${organisationId}/users/activate`,
          {
            method: "POST",
            data: input,
            timeout: STAFF_ACTIVATION_REQUEST_TIMEOUT_MS,
          },
        ),
        selectedUserIds,
      );
      if (normalized.deliveryEvidenceUnconfirmed) {
        return {
          total: 0,
          success: 0,
          failed: 0,
          results: [],
          deliveryEvidenceUnconfirmed: true,
        };
      }
      options?.onBatchProgress?.({ batchIndex: 1, batchSize: normalized.total, activatedSoFar: normalized.success });
      return normalized;
    }

    const aggregate: BulkActivationResponse = {
      total: 0,
      success: 0,
      failed: 0,
      results: [],
    };
    for (
      let offset = 0;
      offset < selectedUserIds.length;
      offset += STAFF_BULK_BATCH_SIZE
    ) {
      const batchUserIds = selectedUserIds.slice(
        offset,
        offset + STAFF_BULK_BATCH_SIZE,
      );
      let result: BulkActivationResponse;
      try {
        result = normalizeActivationEvidence(
          await makeAuthenticatedRequest<BulkActivationResponse>(
            `/admin/organisations/${organisationId}/users/activate`,
            {
              method: "POST",
              data: {
                userIds: batchUserIds,
              },
              timeout: STAFF_ACTIVATION_REQUEST_TIMEOUT_MS,
            },
          ),
          batchUserIds,
        );
      } catch (error) {
        if (aggregate.total === 0) throw error;
        throw withPartialResult(
          "A later activation batch failed after earlier staff were activated",
          aggregate,
          error,
        );
      }
      // Do not continue a large selection after the first delivery failure (or
      // an unexpected missing/skipped outcome). The caller reports the durable
      // partial result and the operator decides whether to proceed.
      const invalidEvidence = result.deliveryEvidenceUnconfirmed === true;
      if (invalidEvidence) {
        // The current batch cannot be credited when its row evidence is
        // structurally disputed. Preserve only earlier confirmed batches.
        aggregate.deliveryEvidenceUnconfirmed = true;
        break;
      }
      aggregate.total += result.total;
      aggregate.success += result.success;
      aggregate.failed += result.failed;
      aggregate.results.push(...result.results);
      options?.onBatchProgress?.({
        batchIndex: offset / STAFF_BULK_BATCH_SIZE + 1,
        batchSize: result.total,
        activatedSoFar: aggregate.success,
      });
      if (result.failed > 0 || hasUnsentActivationEmail(result)) break;
      if (offset + STAFF_BULK_BATCH_SIZE < selectedUserIds.length && options?.shouldStop?.()) {
        aggregate.stoppedByOperator = true;
        break;
      }
    }
    return aggregate;
  }

  if (input.allNotActivated !== true) {
    throw new Error("Bulk activation requires exactly one selection mode");
  }

  const aggregate: BulkActivationResponse = {
    total: 0,
    success: 0,
    failed: 0,
    results: [],
    remainingNotActivated: 0,
  };
  let batchIndex = 0;
  let previousRemaining: number | undefined;
  const seenResultUserIds = new Set<string>();
  do {
    let result: BulkActivationResponse;
    try {
      result = normalizeActivationEvidence(
        await makeAuthenticatedRequest<BulkActivationResponse>(
          `/admin/organisations/${organisationId}/users/activate`,
          {
            method: "POST",
            data: { allNotActivated: true, batchSize: STAFF_BULK_BATCH_SIZE },
            timeout: STAFF_ACTIVATION_REQUEST_TIMEOUT_MS,
          },
        ),
        undefined,
        true,
      );
      if (result.results.some((item) => seenResultUserIds.has(item.userId))) {
        result.deliveryEvidenceUnconfirmed = true;
      }
    } catch (error) {
      if (aggregate.total === 0) throw error;
      throw withPartialResult(
        "A later activation batch failed after earlier staff were activated",
        aggregate,
        error,
      );
    }
    const remaining = result.remainingNotActivated;
    if (
      typeof remaining !== "number" ||
      !Number.isInteger(remaining) ||
      remaining < 0
    ) {
      aggregate.deliveryEvidenceUnconfirmed = true;
      aggregate.remainingNotActivated = undefined;
      throw withPartialResult(
        "Activate All received an invalid remaining staff count. Refresh before retrying",
        aggregate,
        new Error("invalid remainingNotActivated value"),
      );
    }
    if (result.deliveryEvidenceUnconfirmed) {
      aggregate.deliveryEvidenceUnconfirmed = true;
      aggregate.remainingNotActivated = undefined;
      break;
    }
    if (
      remaining > 0 &&
      (result.total === 0 ||
        (previousRemaining !== undefined && remaining >= previousRemaining))
    ) {
      aggregate.deliveryEvidenceUnconfirmed = true;
      aggregate.remainingNotActivated = undefined;
      throw withPartialResult(
        "Activate All made no durable progress. Refresh before retrying",
        aggregate,
        new Error("remaining staged count did not decrease"),
      );
    }

    aggregate.total += result.total;
    aggregate.success += result.success;
    aggregate.failed += result.failed;
    aggregate.results.push(...result.results);
    result.results.forEach((item) => seenResultUserIds.add(item.userId));
    aggregate.remainingNotActivated = remaining;
    previousRemaining = remaining;
    options?.onBatchProgress?.({
      batchIndex: ++batchIndex,
      batchSize: result.total,
      activatedSoFar: aggregate.success,
      remaining,
    });

    // Failed rows remain staged. Stop so the same rows cannot be retried in an
    // unbounded loop; the existing UI reports them for corrective action.
    if (result.failed > 0 || hasUnsentActivationEmail(result)) break;
    if (remaining > 0 && options?.shouldStop?.()) {
      aggregate.stoppedByOperator = true;
      break;
    }
  } while (aggregate.remainingNotActivated > 0);

  return aggregate;
}

export interface ReminderSetting {
  masterCourseId: string;
  masterCourseTitle: string;
  overdueFrom: string | null;
  repeatEveryValue: number | null;
  repeatEveryUnit: "days" | "weeks" | "months" | null;
}

export async function getReminderSettings(
  organisationId: string,
): Promise<ReminderSetting[]> {
  return apiGet<ReminderSetting[]>(
    `/admin/organisations/${organisationId}/reminder-settings`,
  );
}

export async function updateReminderSetting(
  organisationId: string,
  input: {
    masterCourseId: string;
    overdueFrom: string | null;
    repeatEveryValue?: number;
    repeatEveryUnit?: "days" | "weeks" | "months";
  },
): Promise<ReminderSetting | { cleared: true; masterCourseId: string }> {
  return apiPut(
    `/admin/organisations/${organisationId}/reminder-settings`,
    input,
  );
}


export async function exportStaffRosterCSV(organisationId: string, filters: StaffListFilters = {}): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("Not authenticated");
  const params = new URLSearchParams();
  if (filters.search?.trim()) params.set("search", filters.search.trim());
  if (filters.status?.length) params.set("status", filters.status.join(","));
  const base = process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:3001/api";
  const response = await fetch(`${base}/admin/organisations/${encodeURIComponent(organisationId)}/users/export${params.toString() ? `?${params}` : ""}`, {
    headers: { Authorization: `Bearer ${session.access_token}` }, cache: "no-store",
  });
  if (!response.ok) {
    if (response.status === 413) throw new Error("This roster is too large to export from here; contact support.");
    const error = await response.json().catch(() => null);
    throw new Error(typeof error?.message === "string" ? error.message : `Export failed: ${response.statusText}`);
  }
  if (!response.headers.get("Content-Type")?.toLowerCase().startsWith("text/csv")) {
    throw new Error("The service did not return a CSV file. Please try again.");
  }
  const disposition = response.headers.get("Content-Disposition");
  const filename = disposition?.match(/filename="([^"\r\n]+)"/)?.[1];
  if (!filename || filename.includes("/") || filename.includes("\\")) throw new Error("The service did not provide a valid roster filename. Please try again.");
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  try {
    anchor.href = url; anchor.download = filename; document.body.appendChild(anchor); anchor.click();
  } finally {
    anchor.remove(); URL.revokeObjectURL(url);
  }
}
